/**
 * Professional Brand Utilities
 * Centralized utilities for maintaining brand consistency across the application
 */

import logoLandscape from '../assets/stablepay-logo-landscape.png'
import logoPortrait from '../assets/stablepay-logo-portrait.png'
import logoMain from '../assets/StablePay_Logo.png'

export interface BrandColors {
  primary: string
  primaryDark: string
  primaryLight: string
  white: string
  lightGray: string
  success: string
  warning: string
  error: string
  info: string
}

export const brandColors: BrandColors = {
  primary: '#0066CC',
  primaryDark: '#004499',
  primaryLight: '#3399FF',
  white: '#FFFFFF',
  lightGray: '#F8F9FA',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6'
}

export interface LogoVariant {
  src: string
  alt: string
  context: 'header' | 'footer' | 'loading' | 'card' | 'inline'
  theme?: 'light' | 'dark' | 'auto'
}

export const logoVariants: Record<string, LogoVariant> = {
  // Header uses your main logo
  headerLight: {
    src: logoMain,
    alt: 'StablePay Professional Payment Platform',
    context: 'header',
    theme: 'light'
  },
  headerDark: {
    src: logoMain,
    alt: 'StablePay Professional Payment Platform',
    context: 'header',
    theme: 'dark'
  },
  // Footer uses landscape logo for horizontal layout
  footerLight: {
    src: logoLandscape,
    alt: 'StablePay',
    context: 'footer',
    theme: 'light'
  },
  footerDark: {
    src: logoLandscape,
    alt: 'StablePay',
    context: 'footer',
    theme: 'dark'
  },
  // Loading screens use landscape logo for better horizontal layout
  loadingLight: {
    src: logoLandscape,
    alt: 'StablePay',
    context: 'loading',
    theme: 'light'
  },
  loadingDark: {
    src: logoLandscape,
    alt: 'StablePay',
    context: 'loading',
    theme: 'dark'
  },
  // Cards and inline use your main logo
  cardLight: {
    src: logoMain,
    alt: 'StablePay',
    context: 'card',
    theme: 'light'
  },
  cardDark: {
    src: logoMain,
    alt: 'StablePay',
    context: 'card',
    theme: 'dark'
  },
  // Icon context uses your main logo
  iconLight: {
    src: logoMain,
    alt: 'StablePay',
    context: 'inline',
    theme: 'light'
  },
  iconDark: {
    src: logoMain,
    alt: 'StablePay',
    context: 'inline',
    theme: 'dark'
  }
}

/**
 * Get the appropriate logo variant based on context and theme
 */
export const getLogoVariant = (
  context: LogoVariant['context'],
  theme: LogoVariant['theme'] = 'light'
): LogoVariant => {
  const key = `${context}${theme.charAt(0).toUpperCase() + theme.slice(1)}`
  return logoVariants[key] || logoVariants.headerLight
}

/**
 * Generate CSS custom properties for brand colors
 */
export const generateBrandCSSProperties = (): Record<string, string> => {
  return {
    '--stablepay-blue': brandColors.primary,
    '--stablepay-dark-blue': brandColors.primaryDark,
    '--stablepay-light-blue': brandColors.primaryLight,
    '--stablepay-white': brandColors.white,
    '--stablepay-light-gray': brandColors.lightGray,
    '--professional-success': brandColors.success,
    '--professional-warning': brandColors.warning,
    '--professional-error': brandColors.error,
    '--professional-info': brandColors.info
  }
}

/**
 * Professional brand metadata for SEO and PWA
 */
export const brandMetadata = {
  name: 'StablePay',
  fullName: 'StablePay Professional Payment Platform',
  description: 'Professional payment platform enabling Hong Kong SMEs to accept overseas payments via stablecoins with minimal FX costs and instant local bank settlements',
  tagline: 'Professional Payment Platform',
  keywords: [
    'stablecoin',
    'payments',
    'Hong Kong',
    'USDT',
    'USDC',
    'HKD₮',
    'CNH₮',
    'settlement',
    'FPS',
    'CIPS',
    'e-CNY',
    'cross-border payments',
    'financial resilience',
    'provider failover'
  ],
  author: 'StablePay',
  themeColor: brandColors.primary,
  backgroundColor: brandColors.primary,
  locale: 'en-HK',
  social: {
    twitter: '@StablePay',
    linkedin: 'company/stablepay'
  }
}

/**
 * Professional loading messages for different contexts
 */
export const loadingMessages = {
  initialization: [
    'Initializing platform...',
    'Loading professional components...',
    'Connecting to mock APIs...',
    'Preparing demo environment...',
    'Almost ready...'
  ],
  payment: [
    'Processing payment...',
    'Validating transaction...',
    'Connecting to provider...',
    'Confirming settlement...'
  ],
  settlement: [
    'Calculating settlement options...',
    'Checking rail availability...',
    'Optimizing routing...',
    'Preparing settlement...'
  ],
  provider: [
    'Checking provider health...',
    'Testing connections...',
    'Validating failover...',
    'Updating status...'
  ]
}

/**
 * Get appropriate loading message for context
 */
export const getLoadingMessage = (
  context: keyof typeof loadingMessages,
  index?: number
): string => {
  const messages = loadingMessages[context]
  if (index !== undefined && index < messages.length) {
    return messages[index]
  }
  return messages[Math.floor(Math.random() * messages.length)]
}

/**
 * Professional status colors and indicators
 */
export const statusIndicators = {
  success: {
    color: brandColors.success,
    icon: '✓',
    label: 'Success'
  },
  warning: {
    color: brandColors.warning,
    icon: '⚠',
    label: 'Warning'
  },
  error: {
    color: brandColors.error,
    icon: '✗',
    label: 'Error'
  },
  info: {
    color: brandColors.info,
    icon: 'ℹ',
    label: 'Info'
  },
  processing: {
    color: brandColors.primary,
    icon: '⟳',
    label: 'Processing'
  }
}

/**
 * Professional animation durations
 */
export const animationDurations = {
  fast: 150,
  base: 200,
  slow: 300,
  loading: 1000,
  pulse: 2000
}

/**
 * Professional spacing scale
 */
export const spacing = {
  xs: '0.25rem',
  sm: '0.5rem',
  base: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
  '3xl': '4rem'
}

/**
 * Professional typography scale
 */
export const typography = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem'
}